import React, { useState } from 'react';
import { Section, FadeIn, Button } from './ui';
import { ChevronDown, ShieldCheck, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  q: '¿Necesito experiencia previa o conocimientos de nutrición?',
  a: 'No. El plan está diseñado para empezar desde cero, sin conocimientos previos. Cada paso está explicado con claridad para mujeres ocupadas.'
}, {
  q: '¿Cuánto tiempo necesito dedicar cada día?',
  a: 'Entre 5 y 20 minutos diarios. Está diseñado para mujeres con vida ocupada, no necesitas reorganizar tu rutina, solo aplicar micro-ajustes específicos.'
}, {
  q: '¿Funciona si tengo otras condiciones de salud?',
  a: 'Este plan es informativo y complementario. Siempre consulta a tu médico ante condiciones específicas antes de hacer cambios importantes en tu estilo de vida.'
}, {
  q: '¿Cómo recibo el material después de comprar?',
  a: 'Inmediatamente después de tu compra recibes acceso directo a la descarga del PDF: el plan completo más los 3 bonos exclusivos.'
}, {
  q: '¿Qué pasa si no funciona para mí?',
  a: 'Tienes 7 días de garantía total. Si no estás satisfecha, te devolvemos tu dinero completo. Sin preguntas, sin formularios interminables.'
}, {
  q: '¿Es seguro comprar aquí?',
  a: 'Sí. El proceso de pago es 100% seguro y encriptado a través de Hotmart, una de las plataformas más reconocidas del mundo para productos digitales.'
}, {
  q: '¿Debo dejar mis medicamentos actuales?',
  a: 'Absolutamente no. Este plan complementa, no sustituye, cualquier tratamiento médico. Consulta siempre con tu médico antes de hacer cambios en tu medicación.'
}];
const AccordionItem = ({
  q,
  a,
  defaultOpen = false
}: {
  q: string;
  a: string;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return <div className="border-b border-cream-dark last:border-0" data-id="element-105">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none" data-id="element-106">
        <span className="text-lg font-bold text-brown pr-8" data-id="element-107">{q}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-108">
          <ChevronDown className="w-5 h-5 text-gold" data-id="element-109" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-110">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-111">
            <p className="pb-6 text-brown-light" data-id="element-112">{a}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export const FAQSection = () => {
  return <>
      <Section bg="bg-white" data-id="element-113">
        <FadeIn data-id="element-114">
          <div className="max-w-3xl mx-auto" data-id="element-115">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-116">
              Tus preguntas, respondidas
            </h2>
            <div className="bg-cream-light rounded-3xl p-6 md:p-10 shadow-sm" data-id="element-117">
              {faqs.map((faq, i) => <AccordionItem key={i} q={faq.q} a={faq.a} defaultOpen={false} data-id="element-118" />)}
            </div>
          </div>
        </FadeIn>
      </Section>

      <Section bg="bg-cream-dark/30" className="border-t border-cream-dark" data-id="element-119">
        <FadeIn data-id="element-120">
          <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden" data-id="element-121">
            <div className="grid md:grid-cols-2" data-id="element-122">
              <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-center" data-id="element-123">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-124">
                  El cambio que tu cuerpo necesita, al alcance de un clic.
                </h2>
                <p className="text-base text-brown-light mb-6" data-id="element-125">
                  Acceso instantáneo en formato PDF al plan completo más los 3
                  bonos exclusivos.
                </p>

                {/* What's included */}
                <div className="mb-6 bg-cream-light rounded-2xl p-5 border border-cream-dark" data-id="element-126">
                  <p className="text-xs font-bold text-brown uppercase tracking-wider mb-3" data-id="element-127">
                    Incluye hoy:
                  </p>
                  <ul className="space-y-2 text-sm" data-id="element-128">
                    <li className="flex items-start gap-2" data-id="element-129">
                      <Check className="w-4 h-4 text-sage mt-0.5 shrink-0" data-id="element-130" />
                      <span className="text-brown" data-id="element-131">
                        <strong data-id="element-132">Plan de 21 Días</strong>: 5 capítulos + 3
                        herramientas
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-133">
                      <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" data-id="element-134" />
                      <span className="text-brown-light" data-id="element-135">
                        <strong className="text-brown" data-id="element-136">Bono #1:</strong> Reset
                        50+, De Agotada a Encendida en 72h{' '}
                        <span className="text-sage font-bold" data-id="element-137">(GRATIS)</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-138">
                      <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" data-id="element-139" />
                      <span className="text-brown-light" data-id="element-140">
                        <strong className="text-brown" data-id="element-141">Bono #2:</strong> SOS
                        Hormonal, Menú Anticrisis 5 min{' '}
                        <span className="text-sage font-bold" data-id="element-142">(GRATIS)</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-143">
                      <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" data-id="element-144" />
                      <span className="text-brown-light" data-id="element-145">
                        <strong className="text-brown" data-id="element-146">Bono #3:</strong> Ritual
                        Matutino, 5 minutos que reactivan{' '}
                        <span className="text-sage font-bold" data-id="element-147">(GRATIS)</span>
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="mb-6" data-id="element-148">
                  <span className="text-xs text-brown-light uppercase tracking-wider font-bold" data-id="element-149">
                    Plan + 3 Bonos · Inversión única
                  </span>
                  <div className="flex items-baseline gap-3 mt-1" data-id="element-150">
                    <span className="text-2xl text-brown-light/50 line-through font-medium" data-id="element-151">
                      $47
                    </span>
                    <span className="text-5xl font-bold text-coral" data-id="element-152">$17</span>
                    <span className="text-2xl text-brown-light font-normal" data-id="element-153">
                      USD
                    </span>
                  </div>
                  <span className="inline-block mt-2 bg-sage-light text-sage text-sm font-bold px-3 py-1 rounded-full" data-id="element-154">
                    Ahorra más del 60%
                  </span>
                </div>

                <div className="mb-6 flex items-start gap-3 bg-sage-light/40 border border-sage/20 rounded-2xl p-4 text-left" data-id="element-155">
                  <ShieldCheck className="w-6 h-6 text-sage shrink-0 mt-0.5" data-id="element-156" />
                  <p className="text-sm text-brown-light leading-relaxed" data-id="element-157">
                    <span className="font-bold text-brown" data-id="element-158">
                      Garantía de 7 días sin riesgo.
                    </span>{' '}
                    Si no sientes la diferencia te devolvemos cada centavo. Sin
                    preguntas.
                  </p>
                </div>

                <span id="precio" className="block scroll-mt-24" data-id="element-159"></span>
                <Button fullWidth href="https://pay.hotmart.com/W105376697A?checkoutMode=10" onClick={() => {
                try {
                  const fbq = (window as any).fbq;
                  if (fbq) fbq('track', 'AddToCart', {
                    currency: 'USD',
                    value: 17,
                    content_name: 'Plan de 21 Días de Vitalidad Menopáusica',
                    content_type: 'product'
                  });
                } catch (e) {}
              }} data-id="element-160">
                  ✨ SÍ, QUIERO SENTIRME YO OTRA VEZ — $17 USD
                </Button>

                <p className="mt-4 text-sm font-bold text-center text-brown" data-id="element-161">
                  Acceso instantáneo tras la compra. Pago 100% seguro.
                </p>
              </div>

              {/* Right column: product showcase with bonuses */}
              <div className="bg-gradient-to-br from-sage-light/30 to-cream p-6 md:p-8 lg:p-10 flex flex-col items-center justify-center gap-4 relative overflow-hidden" data-id="element-162">
                <div className="absolute inset-0 bg-gradient-to-br from-sage/10 to-transparent" data-id="element-163"></div>

                {/* Main plan image */}
                <div className="relative z-10 w-full" data-id="element-164">
                  <span className="block text-center text-xs font-bold text-gold uppercase tracking-wider mb-2" data-id="element-165">
                    Plan Principal
                  </span>
                  <motion.img src="https://cdn.magicpatterns.com/uploads/qfE7aXZCJfEgvzUdLV7b1P/eBook.png" alt="Plan de 21 Días" className="w-full max-w-xs mx-auto rounded-xl shadow-2xl" animate={{
                  y: [0, -10, 0]
                }} transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-166" />
                </div>

                {/* 3 Bonus thumbnails */}
                <div className="relative z-10 w-full mt-4" data-id="element-167">
                  <div className="flex items-center justify-center gap-2 mb-3" data-id="element-168">
                    <span className="h-px bg-brown-light/30 flex-1" data-id="element-169"></span>
                    <span className="text-xs font-bold text-coral uppercase tracking-wider" data-id="element-170">
                      + 3 Bonos Gratis Hoy
                    </span>
                    <span className="h-px bg-brown-light/30 flex-1" data-id="element-171"></span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 md:gap-3" data-id="element-172">
                    {[{
                    src: 'https://cdn.magicpatterns.com/uploads/3Gaq57BKCUtTjyUKZsJ5YX/Bono_1.png',
                    alt: 'Bono 1: Reset 50+'
                  }, {
                    src: 'https://cdn.magicpatterns.com/uploads/xA4XtyjAKJK63Jo8zG4njz/Bono_2..png',
                    alt: 'Bono 2: SOS Hormonal'
                  }, {
                    src: 'https://cdn.magicpatterns.com/uploads/4daWHgzJb95gMad7kD3S9D/Bono_3.png',
                    alt: 'Bono 3: Ritual Matutino'
                  }].map((bonus, idx) => <motion.div key={idx} className="aspect-[16/9] bg-cream-light rounded-lg overflow-hidden shadow-md" animate={{
                    y: [0, -7, 0]
                  }} transition={{
                    duration: 4.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: idx * 0.6
                  }} data-id="element-173">
                        <img src={bonus.src} alt={bonus.alt} className="w-full h-full object-cover" data-id="element-174" />
                      </motion.div>)}
                  </div>
                  <p className="text-center text-xs text-brown-light/70 mt-3 italic" data-id="element-175">
                    Valor combinado:{' '}
                    <span className="line-through" data-id="element-176">$51 USD</span>
                  </p>
                </div>

                {/* Reassurance list to fill the block */}
                <div className="relative z-10 w-full mt-6 pt-6 border-t border-brown-light/15" data-id="element-177">
                  <ul className="space-y-3 max-w-xs mx-auto" data-id="element-178">
                    {['Plan completo de 21 días paso a paso', 'Los 3 bonos exclusivos incluidos hoy', 'Acceso instantáneo y de por vida', 'Garantía de 7 días sin riesgo'].map((item, idx) => <li key={idx} className="flex items-start gap-3 text-sm text-brown" data-id="element-179">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sage-light flex items-center justify-center mt-0.5" data-id="element-180">
                          <Check className="w-3 h-3 text-sage" data-id="element-181" />
                        </span>
                        <span className="leading-relaxed" data-id="element-182">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>
    </>;
};